import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import CheckIcon from '@material-ui/icons/Check';
import InfoIcon from '@material-ui/icons/Info';
import Tooltip from '@material-ui/core/Tooltip';
import Icon from '@cpg-its/ads.common.icon';
import { yellow } from '@cpg-its/ads.constants.colors';
import { getFormattedDate } from '../../global/helpers/date-helpers';
import { Notification, NotificationStatus } from './notification-models';
import { WysiwygWithReactLink } from '../../global/components/wysiwyg-with-react-link';
import { dismissNotification } from './notification-helpers';

interface Props {
	notification: Notification;
	onDismiss?: (notification: Notification) => void;
	mandatoryTooltip?: string;
	animateRemoval?: boolean;
}

const REMOVE_ANIMATION_MS = 300;

export function NotificationItem({ notification, onDismiss, mandatoryTooltip, animateRemoval }: Props) {
	const dispatch = useDispatch();
	const [isRemoving, setIsRemoving] = useState(false);
	const itemRef = useRef<HTMLDivElement>(null);
	const isInitial = notification?.notification_status === NotificationStatus.INITIAL;
	// temporary, might remove: Mandatory styling only applies while the notification is still INITIAL.
	//  Once it's complete/dismissed/processed it renders like a read item.
	const isMandatory = !!notification?.is_mandatory && isInitial;
	const isRead = !isMandatory && !isInitial;

	const performDismiss = () => {
		if (onDismiss) {
			onDismiss(notification);
		} else {
			dismissNotification(dispatch, notification);
		}
	};

	const markAsRead = () => {
		if (isMandatory || isRemoving) return;
		if (animateRemoval && itemRef.current) {
			const el = itemRef.current;
			el.style.maxHeight = `${el.scrollHeight}px`;
			el.getBoundingClientRect();
			setIsRemoving(true);
			window.setTimeout(performDismiss, REMOVE_ANIMATION_MS);
		} else {
			performDismiss();
		}
	};

	const getItemClassName = () => {
		const base = 'notification-item';
		const removing = isRemoving ? ` ${base}--removing` : '';
		if (isMandatory) return `${base} ${base}--mandatory${removing}`;
		if (isRead) return `${base} ${base}--read${removing}`;
		return `${base} ${base}--unread${removing}`;
	};

	return (
		<div className={getItemClassName()} ref={itemRef}>
			<div className="notification-item-icon">
				<Icon color={yellow} icon="warning" size="large" />
			</div>
			<div className="notification-item-content">
				<div className="notification-item-header">
					<h6 className="notification-item-subject">{notification?.notification_subject}</h6>
					<div className="notification-item-header-right">
						<span className="notification-item-date">{getFormattedDate(notification?.create_timestamp?.toString(), false, true)}</span>
					</div>
				</div>
				<div className="notification-item-body">
					<WysiwygWithReactLink content={notification?.notification_body} dispatch={dispatch} />
				</div>
			</div>
			<div className="notification-item-action">
				{isMandatory && (
					<span className="notification-item-mandatory-badge">
						Mandatory
						<Tooltip title={mandatoryTooltip || 'This notification is mandatory and cannot be dismissed.'} arrow placement="top">
							<InfoIcon className="notification-item-mandatory-info-icon" />
						</Tooltip>
					</span>
				)}
				{!isMandatory && isRead && (
					<span className="notification-item-read-label">
						<CheckIcon className="notification-item-read-icon" />
						Read
					</span>
				)}
				{!isMandatory && !isRead && (
					<button type="button" className="notification-item-mark-read-btn" onClick={markAsRead}>
						Mark as read
					</button>
				)}
			</div>
		</div>
	);
}

NotificationItem.defaultProps = {
	onDismiss: undefined,
	mandatoryTooltip: undefined,
	animateRemoval: false,
};
